<template>
  <FormCard
    v-for="fieldGroup in filteredFieldGroups"
    :key="fieldGroup.key"
    :data-testid="`self-service-form-adform-group-${fieldGroup.key}`"
    :data-group-key="fieldGroup.key"
    :title="fieldGroup.name"
    :class="{ 'selected-card': openCard === fieldGroup.key }"
    :is-open="openCard === fieldGroup.key"
    @toggle="handleFormCardToggle(fieldGroup.key)"
    :completed="completedCards.has(fieldGroup.key)"
    @enter-pressed="handleSaveAndContinue(fieldGroup.key)">
    <template #form-content>
      <div
        class="form-section"
        v-for="field in fieldGroup.properties"
        :key="field.key"
        :data-testid="`self-service-field-${field.key}`"
        :data-field-key="field.key">
        <AdformTargeting
          v-if="field.type === 'locations' && field.key === 'target_locations' && providerId"
          v-model="obj[field.key]"
          :provider-id="providerId"
          :errors="errors[field.key] || []"
          :default="defaultTargetLocationObject" />

        <LeadSelect
          v-else-if="field.type === 'select'"
          v-model="obj[field.key]"
          :options="field?.options || []"
          :label="field.label"
          :errors="errors[field.key] || []" />
      </div>
      <div
        class="form-actions float-right"
        :data-testid="`self-service-save-action-${fieldGroup.key}`"
        :data-i18n-key="isLast(fieldGroup.key) ? 'btn.save' : 'btn.save_and_continue'">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleSaveAndContinue(fieldGroup.key)">
          {{ isLast(fieldGroup.key) ? $t('btn.save') : $t('btn.save_and_continue') }}
          <template v-if="!isLast(fieldGroup.key)" #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
  <FormCard
    key="creative"
    data-testid="self-service-form-adform-group-creative"
    data-group-key="creative"
    title="Creative"
    :class="{ 'selected-card': openCard === 'creative' }"
    :is-open="openCard === 'creative'"
    @toggle="handleFormCardToggle('creative')"
    :completed="completedCards.has('creative')"
    @enter-pressed="handleCreativeSave('creative')">
    <template #form-content>
      <div class="form-section" data-testid="self-service-field-banners" data-field-key="banners">
        <AdformCreative v-model="banners" :provider-id="providerId" :errors="errors['main_banner']" />
      </div>
      <div class="form-actions float-right" data-testid="self-service-save-action-creative" data-i18n-key="btn.save">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleCreativeSave('creative')">
          {{ $t('btn.save') }}
          <template #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted, nextTick, type Ref } from 'vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadSelect from '@/components/form/LeadSelect.vue';
import { IconArrowBigRightFilled } from '@tabler/icons-vue';
import { updateCampaignObject } from '@/api/campaign';
import type { FieldGroup } from '@/types/types';
import AdformTargeting from '@/components/form/AdformTargeting.vue';
import AdformCreative from '@/components/form/AdformCreative.vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import translator from '@/locale/translator';

const defaultTargetLocationObject = {
  label: translator.get('campaigns.please_select_location'),
  id: 0,
  type: '',
};

export type LocationItem = typeof defaultTargetLocationObject;

interface AdformForm {
  target_locations: Array<{
    label: string;
    id: number;
    type: string;
  }>;
  main_banner: number[];
  [key: string]: any;
}

const props = defineProps<{
  providerId: string | null | undefined;
  fieldGroups: FieldGroup[];
  object: any;
}>();

const emit = defineEmits<{
  (e: 'update:object', obj: any): void;
}>();

const openCard = ref<string | null>(null);
const providerStore = useProviderStore();
const { completedDecks } = storeToRefs(providerStore);
const loadingSave = ref(false);
const { obj } = storeToRefs(providerStore) as unknown as { obj: Ref<AdformForm> };

const completedCards = computed(() => {
  if (!props.providerId) return new Set<string>();
  return completedDecks.value[props.providerId] || new Set<string>();
});

const errors = ref({});
const filteredFieldGroups = ref<FieldGroup[]>([]);

// banner selection is proxied through the shared provider store so the preview
// panel (which deep-watches `obj`) re-renders on every pick instead of only on save.
// the setter always writes a fresh array so the watcher fires reliably.
const banners = computed<number[]>({
  get: () => (Array.isArray(obj.value.main_banner) ? obj.value.main_banner : []),
  set: (value) => {
    obj.value.main_banner = [...(value || [])];
  },
});

onMounted(async () => {
  // Open the first card by default if field groups are available
  if (props.fieldGroups.length > 0 && !openCard.value) {
    openCard.value = props.fieldGroups[0].key;
  }
});

const handleFormCardToggle = (key: string) => {
  // If the clicked card is already open, close it
  if (openCard.value === key) {
    openCard.value = null;
  } else {
    // Otherwise, open the clicked card (and close any other open card)
    openCard.value = key;
  }
};

const handleSaveAndContinue = async (currentCard: string) => {
  loadingSave.value = true;
  // Update the object with the values of the current card
  const updateData = {};
  props.object.groups.forEach((group) => {
    if (group.key === currentCard) {
      Object.values(group.properties).map((property: any) => {
        if (obj.value[property.key] !== undefined) {
          if (property.key === 'target_locations') {
            updateData[property.key] = obj.value[property.key].filter((loc: any) => loc.id || loc.type);
            if (updateData[property.key].length > 0) updateData['location_targeting'] = 1;
          } else updateData[property.key] = obj.value[property.key];
        }
      });
    }
  });

  try {
    const res = await updateCampaignObject(props.providerId ?? '', updateData);

    emit('update:object', res.data);

    // Clear any previous errors on success
    errors.value = {};
    setTimeout(() => {
      loadingSave.value = false;
      providerStore.addCompletedCard(currentCard, props.providerId ?? '');

      const currentIndex = filteredFieldGroups.value.findIndex((g) => g.key === currentCard);
      if (currentIndex >= 0 && currentIndex < filteredFieldGroups.value.length - 1) {
        openCard.value = filteredFieldGroups.value[currentIndex + 1].key;
      } else {
        openCard.value = 'creative';
      }
    }, 500);
  } catch (error: any) {
    if (error.responseJson) {
      errors.value = error.responseJson.errors?.provider_values || {};
    } else {
      console.error('Unexpected error:', error);
    }
    loadingSave.value = false;
  }
};

// creatives save through the same endpoint, but under `main_banner` — the group schema exposes them
// as `banners`, which is not writable, so the generic card save cannot carry them
const handleCreativeSave = async (currentCard: string) => {
  try {
    loadingSave.value = true;
    const res = await updateCampaignObject(props.providerId ?? '', { main_banner: banners.value });
    emit('update:object', res.data);

    setTimeout(() => {
      loadingSave.value = false;
      providerStore.addCompletedCard(currentCard, props.providerId ?? '');
      errors.value = {};
      openCard.value = null;
    }, 500);
  } catch (error: any) {
    if (error.responseJson) {
      errors.value = error.responseJson.errors?.provider_values || {};
    } else {
      console.error('Unexpected error:', error);
    }
    loadingSave.value = false;
  }
};

const isLast = (key: string) => {
  const lastIndex = props.fieldGroups.length - 1;
  return props.fieldGroups[lastIndex].key === key;
};

watch(
  () => props.fieldGroups,
  (newValue) => {
    if (newValue) {
      filteredFieldGroups.value = props.fieldGroups.filter((group) => group.key !== 'creative');

      openCard.value = null;
      if (props.fieldGroups.length > 0 && !openCard.value) {
        nextTick(() => {
          openCard.value = props.fieldGroups[0].key;
        });

        props.object.groups.map((group) => {
          if (props.object?.confirmed || props.object?.values_valid) {
            providerStore.addCompletedCard(group.key, props.providerId ?? '');
          }

          if (group.properties) {
            Object.values(group.properties).map((property: any) => {
              if (property.key === 'banners') {
                // creatives are named differently in each direction: the campaign sends them as
                // `banners` (campaign banner objects), while saving and previewing them both expect
                // `main_banner` (plain banner ids). there is no `main_banner` property to read, so it
                // has to be mapped out of each row's `banner_id`.
                // seeding it here also scopes the selection to this campaign. the store outlives
                // navigation, so otherwise a selection made on a previously opened campaign stays
                // behind and gets previewed instead of this one's (PJAS-4995).
                obj.value.main_banner = Array.isArray(property.value)
                  ? property.value.map((campaignBanner: any) => campaignBanner.banner_id).filter(Boolean)
                  : [];
                return;
              }

              obj.value[property.key] = property.value || '';

              nextTick(() => {
                // Ensure at least one default location
                if (obj.value?.target_locations.length === 0) {
                  obj.value.target_locations = [defaultTargetLocationObject];
                }
              });
            });
          }
        });
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}
</style>
